import { useState, useEffect } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import {
  Phone, Send, Users, Clock, RefreshCw, Plus, Trash2,
  CheckCircle, AlertTriangle, MessageSquare, Settings
} from 'lucide-react';

export default function SMSManagement() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('compose');
  const [sending, setSending] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [history, setHistory] = useState([]);
  const [phones, setPhones] = useState(['']);
  const [message, setMessage] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configRes, histRes] = await Promise.all([
        api.get('/sms/config'),
        api.get('/sms/history').catch(() => ({ data: [] }))
      ]);
      setConfigured(configRes.data?.configured || false);
      setHistory(histRes.data || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const addPhone = () => setPhones(p => [...p, '']);
  const removePhone = (idx) => setPhones(p => p.filter((_, i) => i !== idx));
  const updatePhone = (idx, val) => setPhones(p => p.map((ph, i) => i === idx ? val : ph));

  const sendBulkSMS = async () => {
    const validPhones = phones.filter(p => p.trim());
    if (validPhones.length === 0) {
      toast.error('Adicione pelo menos um número de telefone.');
      return;
    }
    if (!message.trim()) {
      toast.error('Escreva a mensagem SMS.');
      return;
    }
    if (message.length > 160) {
      toast.error('Mensagem demasiado longa (máx. 160 caracteres).');
      return;
    }

    setSending(true);
    try {
      if (validPhones.length === 1) {
        await api.post('/sms/send', { phone: validPhones[0], message });
      } else {
        await api.post('/sms/bulk', { phones: validPhones, message });
      }
      toast.success(`SMS enviado para ${validPhones.length} destinatário(s)`);
      setPhones(['']);
      setMessage('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar SMS');
    } finally {
      setSending(false);
    }
  };

  const sendTestSMS = async () => {
    if (!testPhone.trim()) {
      toast.error('Introduza um número para teste.');
      return;
    }
    setTesting(true);
    try {
      const res = await api.post(`/sms/test?phone=${encodeURIComponent(testPhone)}`);
      if (res.data.success) {
        toast.success('SMS de teste enviado com sucesso!');
      } else {
        toast.error('Falha ao enviar SMS de teste. Verifique as credenciais Twilio.');
      }
    } catch (err) {
      toast.error('Erro ao testar SMS.');
    } finally {
      setTesting(false);
    }
  };

  const templates = [
    {
      name: 'Lembrete de Agendamento',
      message: 'Embaixada de Angola: Recordamos o seu agendamento consular. Compareça com documentos necessários.'
    },
    {
      name: 'Documento Pronto',
      message: 'Embaixada de Angola: O seu documento está pronto para levantamento. Horário: 09h-14h.'
    },
    {
      name: 'Comunicado Urgente',
      message: 'Embaixada de Angola em Espanha: Comunicado urgente. Consulte o portal para mais informações.'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-gray-400" size={24} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sms-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notificações SMS</h1>
          <p className="text-sm text-gray-500 mt-1">
            Envio de SMS via Twilio para utentes e cidadãos
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full ${
            configured ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
          }`} data-testid="sms-config-status">
            {configured ? <CheckCircle size={12} /> : <AlertTriangle size={12} />}
            {configured ? 'Twilio Configurado' : 'Twilio Não Configurado'}
          </span>
          <button onClick={fetchData} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50">
            <RefreshCw size={18} className="text-gray-600" />
          </button>
        </div>
      </div>

      {/* Warning if not configured */}
      {!configured && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3" data-testid="twilio-warning">
          <AlertTriangle size={20} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">Twilio não configurado</p>
            <p className="text-xs text-amber-600 mt-1">
              Para enviar SMS, configure as variáveis <code>TWILIO_ACCOUNT_SID</code>, <code>TWILIO_AUTH_TOKEN</code> e <code>TWILIO_PHONE_NUMBER</code> no ficheiro .env do backend.
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {[
          { id: 'compose', label: 'Compor SMS', icon: MessageSquare },
          { id: 'history', label: 'Histórico', icon: Clock },
          { id: 'settings', label: 'Configuração', icon: Settings },
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`sms-tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-[#CC0000] text-[#CC0000]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Compose Tab */}
      {activeTab === 'compose' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Compor SMS</h3>

            {/* Phone numbers */}
            <div className="space-y-3 mb-4">
              <label className="block text-sm font-medium text-gray-700">Destinatários</label>
              {phones.map((phone, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div className="flex-1 relative">
                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => updatePhone(idx, e.target.value)}
                      placeholder="+34 612 345 678"
                      className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20"
                      data-testid={`sms-phone-${idx}`}
                    />
                  </div>
                  {phones.length > 1 && (
                    <button
                      onClick={() => removePhone(idx)}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      data-testid={`remove-phone-${idx}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={addPhone}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg"
                data-testid="add-phone-btn"
              >
                <Plus size={14} /> Adicionar número
              </button>
            </div>

            {/* Message */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">Mensagem</label>
                <span className={`text-xs ${message.length > 160 ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                  {message.length}/160
                </span>
              </div>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Escreva a mensagem SMS..."
                rows={4}
                maxLength={320}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20"
                data-testid="sms-message"
              />
            </div>

            {/* Send button */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-500">
                {phones.filter(p => p.trim()).length} destinatário(s)
              </p>
              <button
                onClick={sendBulkSMS}
                disabled={sending || !configured}
                className="flex items-center gap-2 px-6 py-2 bg-[#CC0000] text-white text-sm font-medium rounded-lg hover:bg-[#A30000] disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="send-sms-btn"
              >
                {sending ? <RefreshCw size={16} className="animate-spin" /> : <Send size={16} />}
                {sending ? 'A enviar...' : 'Enviar SMS'}
              </button>
            </div>
          </div>

          {/* Templates */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Templates</h3>
            <div className="space-y-3">
              {templates.map((tpl, idx) => (
                <button
                  key={idx}
                  onClick={() => { setMessage(tpl.message); toast.success('Template aplicado'); }}
                  className="w-full p-3 text-left border border-gray-200 rounded-lg hover:border-[#CC0000] hover:bg-red-50/30 transition-colors"
                  data-testid={`sms-template-${idx}`}
                >
                  <p className="text-sm font-medium text-gray-900">{tpl.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{tpl.message}</p>
                </button>
              ))}
            </div>
            <div className="mt-6 p-4 bg-amber-50 rounded-lg">
              <p className="text-xs text-amber-800">
                <strong>Nota:</strong> SMS são enviados via Twilio. Custos aplicáveis por mensagem.
                Máximo de 160 caracteres por SMS.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-2">
              <Clock size={18} className="text-gray-500" />
              <span className="font-medium text-gray-900">Histórico de Envios SMS</span>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {history.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <MessageSquare size={40} className="mx-auto mb-3 opacity-30" />
                <p>Nenhum SMS enviado</p>
              </div>
            ) : (
              history.map((item, idx) => (
                <div key={idx} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {item.bulk ? `${item.recipient_count} destinatários` : item.phone}
                      </p>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.message}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        item.status === 'queued' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {item.status === 'queued' ? 'Enviado' : item.status}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(item.sent_at).toLocaleDateString('pt-PT')}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Testar Configuração</h3>
          <p className="text-sm text-gray-500 mb-4">
            Envie um SMS de teste para verificar se o Twilio está a funcionar corretamente.
          </p>
          <div className="flex items-end gap-3 max-w-md">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Número de teste</label>
              <input
                type="tel"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="+34 612 345 678"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20"
                data-testid="test-phone-input"
              />
            </div>
            <button
              onClick={sendTestSMS}
              disabled={testing || !configured}
              className="flex items-center gap-2 px-5 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50"
              data-testid="test-sms-btn"
            >
              {testing ? <RefreshCw size={16} className="animate-spin" /> : <Phone size={16} />}
              {testing ? 'A testar...' : 'Enviar Teste'}
            </button>
          </div>

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Estado da Configuração</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${configured ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-sm text-gray-700">Twilio Account SID</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${configured ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-sm text-gray-700">Twilio Auth Token</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${configured ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-sm text-gray-700">Twilio Phone Number</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
